import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { Conversation, ConversationContent, ConversationScrollButton } from "../../ai-elements/conversation.js";
import { memo, useState, useMemo, useCallback, useEffect } from "react";
import { useConversationScrollDetection } from "../hooks/useConversationScrollDetection.js";
import ConciergeWelcome from "./ConciergeWelcome.js";
import { ConversationSkeleton } from "./ConversationSkeleton.js";
import { MessageListV2 } from "./MessageListV2.js";
import { PromptInputConcierge } from "./PromptInputConcierge.js";
import { ConciergeSidebar } from "./ConciergeSidebar.js";
import { ConciergeHomeMobileNav } from "./ConciergeHomeMobileNav.js";
import { useWidgetConfig, customColor, useResolveColor, ConciergePanelWidget } from "@brandclub/bc-widgets";
const MOBILE_BREAKPOINT = 768;
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}
const ConciergeInputArea = ({
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  conversationHistoryLength,
  onTrackAccessPoint
}) => {
  const placeholder = useMemo(
    () => conversationHistoryLength > 0 ? "Ask a follow up question" : "Ask me anything",
    [conversationHistoryLength]
  );
  const handleSearchWithTracking = useCallback(
    (text, imageData, imageMimeType) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("search_bar", text);
      handleSearch(text, imageData, imageMimeType);
    },
    [handleSearch, onTrackAccessPoint]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4",
      style: { paddingBottom: "max(1rem, env(safe-area-inset-bottom))" },
      children: /* @__PURE__ */ jsx(
        PromptInputConcierge,
        {
          placeholder,
          value: searchInputValue,
          onChange: setSearchInputValue,
          onSubmit: handleSearchWithTracking,
          isStreaming: isActivelyStreaming
        }
      )
    }
  );
};
const ConciergeChatAreaV2Component = ({
  appName,
  brandId,
  conversationId,
  isEmbedded = false,
  apiBaseUrl,
  userFirstName,
  pageContext,
  conversationHistory,
  productMetadataMap,
  surveyMetadataMap,
  streamingProductGroups,
  previewSearchQueries,
  previewQueryProducts,
  followUpQuestions,
  streamingFeaturedProduct,
  streamingStoreLocator,
  streamingLoginRequired,
  streamingDevInfo,
  onSignIn,
  isActivelyStreaming,
  streamingAIResponse,
  currentStatus,
  isWaitingForFollowUpQueries,
  isLoadingConversationQuery,
  isPendingAssistantResponse,
  isPollingTimedOut,
  onRetry,
  handleSearch,
  onUpdateFeedback,
  onVariantSelect,
  onTrackAccessPoint,
  onHomeClick,
  isSidebarExpanded = true,
  brandLogoUrl,
  searchInputValue,
  setSearchInputValue,
  mobileSidebarProps,
  isSharedView,
  sharedByUserName,
  sourceConversationId,
  sourceCheckpointIndex
}) => {
  const isMobile = useIsMobile();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery;
  const hasImageCarousel = false;
  const showTypingIndicator = isActivelyStreaming && !streamingAIResponse && previewSearchQueries.length === 0 && previewQueryProducts.length === 0;
  const scrollDetectionParams = useMemo(
    () => ({
      followUpQuestions,
      isActivelyStreaming,
      conversationHistoryLength: conversationHistory.length,
      showWelcome,
      searchInputValue: ""
      // Not actually used in the hook, but required by interface
    }),
    [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome]
  );
  const { bottomRef } = useConversationScrollDetection(scrollDetectionParams);
  const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);
  const handleWelcomeSearch = useCallback(
    (searchTerm) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("quick_prompts", searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint]
  );
  const handleFollowUpSearch = useCallback(
    (searchTerm) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("follow_up_question", searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint]
  );
  const welcomeProps = useMemo(
    () => ({
      onSearch: handleWelcomeSearch,
      brandId: brandId ? parseInt(brandId) : void 0,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext,
      brandLogoUrl,
      brandName: appName,
      // Pass input state so the home screen renders the input as part of its centered content
      searchInputValue,
      setSearchInputValue,
      handleSearch,
      isActivelyStreaming,
      isMobile
    }),
    [
      handleWelcomeSearch,
      brandId,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext,
      brandLogoUrl,
      appName,
      searchInputValue,
      setSearchInputValue,
      handleSearch,
      isActivelyStreaming,
      isMobile
    ]
  );
  const SimpleWelcomeHeader = useMemo(
    () => /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center", children: [
      /* @__PURE__ */ jsx("p", { className: "tw:font-bold tw:text-[16px] tw:leading-[32px] tw:tracking-[2px] tw:uppercase tw:text-primary", children: "CONCIERGE" }),
      /* @__PURE__ */ jsxs("h1", { className: "tw:font-semibold tw:text-[24px] tw:leading-[32px] tw:tracking-[0.08px] tw:mt-4 tw:text-primary", children: [
        "Let us help you find what you've been looking for",
        userFirstName ? `, ${userFirstName}` : "",
        "."
      ] })
    ] }),
    [userFirstName]
  );
  const conversationContent = useMemo(() => {
    if (isLoadingConversationQuery) {
      return /* @__PURE__ */ jsx(ConversationSkeleton, {});
    }
    if (showWelcome) {
      return /* @__PURE__ */ jsx(ConciergeWelcome, { ...welcomeProps });
    }
    return /* @__PURE__ */ jsx(
      MessageListV2,
      {
        conversationId,
        messages: conversationHistory,
        showTypingIndicator,
        streamingAIResponse,
        productMetadataMap,
        surveyMetadataMap,
        streamingProductGroups,
        currentStatus,
        previewSearchQueries,
        previewQueryProducts,
        isWaitingForFollowUpQueries,
        isActivelyStreaming,
        streamingFeaturedProduct,
        streamingStoreLocator,
        streamingLoginRequired,
        streamingDevInfo,
        onSignIn,
        followUpQuestions,
        onSearch: handleFollowUpSearch,
        onUpdateFeedback,
        onVariantSelect,
        bottomRef,
        apiBaseUrl,
        isPendingAssistantResponse,
        isPollingTimedOut,
        onRetry,
        isSharedView,
        sharedByUserName,
        sourceConversationId,
        sourceCheckpointIndex
      }
    );
  }, [
    isLoadingConversationQuery,
    showWelcome,
    hasImageCarousel,
    SimpleWelcomeHeader,
    welcomeProps,
    conversationId,
    conversationHistory,
    showTypingIndicator,
    streamingAIResponse,
    productMetadataMap,
    streamingProductGroups,
    currentStatus,
    previewSearchQueries,
    previewQueryProducts,
    isWaitingForFollowUpQueries,
    isActivelyStreaming,
    streamingFeaturedProduct,
    streamingStoreLocator,
    streamingLoginRequired,
    streamingDevInfo,
    onSignIn,
    followUpQuestions,
    handleFollowUpSearch,
    onUpdateFeedback,
    onVariantSelect,
    bottomRef,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex
  ]);
  const directPanelState = useWidgetConfig("concierge-panel", {
    preset: "vertical-compact",
    mainBgColor: customColor("transparent"),
    pillLayout: "vertical",
    pillShowIcon: false,
    pillBorderRadius: 8,
    pillBorderColor: customColor("#e5e7eb"),
    pillBorderWidth: 1,
    pillBgColor: customColor("#ffffff"),
    pillTextColor: customColor("#181d56"),
    pillShadowEnabled: false,
    pillShadow: "8px 8px 14px 0 rgba(0, 0, 0, 0.03)",
    pillContainerBgColor: customColor("transparent"),
    pillContainerBorderRadius: 0,
    inputBorderColor: customColor("#e5e7eb"),
    inputBorderWidth: 1,
    inputBorderRadius: 100,
    inputMinHeight: 0,
    inputShadowEnabled: false,
    inputShadow: "8px 8px 14px 0 rgba(0, 0, 0, 0.03)"
  });
  const directResolveColor = useResolveColor();
  const renderContent = (panelState, resolveColor) => {
    const bgColor = resolveColor(panelState.mainBgColor);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: "tw:relative tw:flex tw:flex-1 tw:flex-col",
        style: {
          minHeight: 0,
          maxHeight: "100%",
          overflow: "hidden",
          ...bgColor && bgColor !== "transparent" ? { backgroundColor: bgColor } : {}
        },
        children: [
          isMobile && mobileSidebarProps && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                position: "absolute",
                inset: 0,
                zIndex: 50,
                display: "flex",
                pointerEvents: isMobileSidebarOpen ? "auto" : "none"
              },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.3)",
                      opacity: isMobileSidebarOpen ? 1 : 0,
                      transition: "opacity 0.25s ease-in-out"
                    },
                    onClick: () => setIsMobileSidebarOpen(false)
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      position: "relative",
                      zIndex: 1,
                      height: "100%",
                      transform: isMobileSidebarOpen ? "translateX(0)" : "translateX(-100%)",
                      transition: "transform 0.25s ease-in-out"
                    },
                    children: /* @__PURE__ */ jsx(
                      ConciergeSidebar,
                      {
                        ...mobileSidebarProps,
                        isExpanded: true,
                        onToggle: () => setIsMobileSidebarOpen(false),
                        mobileOverlay: true
                      }
                    )
                  }
                )
              ]
            }
          ),
          isMobile && mobileSidebarProps && /* @__PURE__ */ jsx(
            ConciergeHomeMobileNav,
            {
              brandLogoUrl,
              onMenuClick: () => setIsMobileSidebarOpen(true),
              onCloseClick: onHomeClick
            }
          ),
          isEmbedded && showWelcome ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  maxHeight: "100%"
                },
                children: /* @__PURE__ */ jsx("div", { style: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }, children: conversationContent })
              }
            ),
            /* @__PURE__ */ jsx(
              ConciergeInputArea,
              {
                searchInputValue,
                setSearchInputValue,
                handleSearch,
                isActivelyStreaming,
                conversationHistoryLength: conversationHistory.length,
                onTrackAccessPoint
              }
            )
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  maxHeight: "100%"
                },
                children: showWelcome ? /* @__PURE__ */ jsx("div", { style: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }, children: conversationContent }) : /* @__PURE__ */ jsxs(Conversation, { children: [
                  /* @__PURE__ */ jsx(ConversationContent, { className: "tw:flex tw:items-center tw:justify-center", children: conversationContent }),
                  /* @__PURE__ */ jsx(ConversationScrollButton, {})
                ] })
              }
            ),
            (isEmbedded || !showWelcome) && /* @__PURE__ */ jsx(
              ConciergeInputArea,
              {
                searchInputValue,
                setSearchInputValue,
                handleSearch,
                isActivelyStreaming,
                conversationHistoryLength: conversationHistory.length,
                onTrackAccessPoint
              }
            )
          ] })
        ]
      }
    );
  };
  if (isEmbedded) {
    return /* @__PURE__ */ jsx(
      ConciergePanelWidget,
      {
        widgetId: "concierge-panel",
        displayName: "Concierge Panel",
        suggestionListWidgetId: "concierge-suggestion-list",
        chatInputWidgetId: "concierge-chat-input",
        suppressChildEdits: false,
        children: (panelState, resolveColor) => renderContent(panelState, resolveColor)
      }
    );
  }
  return renderContent(directPanelState, directResolveColor);
};
const ConciergeChatAreaV2 = memo(
  ConciergeChatAreaV2Component
  // (prevProps, nextProps) => {
  //   // Skip re-render if only searchInputValue changed
  //   return (
  //     prevProps.conversationHistory === nextProps.conversationHistory &&
  //     prevProps.isActivelyStreaming === nextProps.isActivelyStreaming &&
  //     prevProps.streamingAIResponse === nextProps.streamingAIResponse &&
  //     prevProps.isLoadingConversationQuery ===
  //       nextProps.isLoadingConversationQuery &&
  //     prevProps.followUpQuestions === nextProps.followUpQuestions &&
  //     prevProps.previewSearchQueries === nextProps.previewSearchQueries &&
  //     prevProps.previewQueryProducts === nextProps.previewQueryProducts &&
  //     prevProps.streamingFeaturedProduct ===
  //       nextProps.streamingFeaturedProduct &&
  //     prevProps.productMetadataMap === nextProps.productMetadataMap &&
  //     prevProps.streamingProductGroups === nextProps.streamingProductGroups &&
  //     prevProps.currentStatus === nextProps.currentStatus &&
  //     prevProps.isWaitingForFollowUpQueries ===
  //       nextProps.isWaitingForFollowUpQueries &&
  //     prevProps.conversationId === nextProps.conversationId &&
  //     prevProps.brandId === nextProps.brandId &&
  //     prevProps.isEmbedded === nextProps.isEmbedded &&
  //     prevProps.appName === nextProps.appName &&
  //     prevProps.handleSearch === nextProps.handleSearch &&
  //     prevProps.onRetry === nextProps.onRetry &&
  //     prevProps.onUpdateFeedback === nextProps.onUpdateFeedback &&
  //     prevProps.setSearchInputValue === nextProps.setSearchInputValue
  //     // Intentionally NOT comparing searchInputValue - input is in separate component
  //   );
  // }
);
export {
  ConciergeChatAreaV2
};
//# sourceMappingURL=ConciergeChatAreaV2.js.map
